import { Label } from '@components/UI-Components/Label'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { FontTShirtSize } from '@modules/common/models/enums/FontSize'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Popover from '@material-ui/core/Popover'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { stores } from '@stores'
import findIndex from 'lodash/findIndex'
import isEmpty from 'lodash/isEmpty'
import memoize from 'memoize-one'
import { useObserver } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { AutoSizer } from 'react-virtualized'
import { areEqual, VariableSizeList as List } from 'react-window'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import * as css from './ClusterExclusionMultiSelect.css'

interface Cluster {
  Id: string
  Name: string
  RGBHex: string
  __typename: string
}

interface ClusterExclusionMultiSelectProps {
  clusterData: Cluster[]
  excludedClusters: Cluster[]
  addedClusters: Cluster[]
  handleApply: any
  isActionInProcess: boolean
  showDisabledExcludedClustersPopOver: boolean
  anchorEl?: any
  getAssortmentExcludedClusters?: () => Cluster[]
}

const TABLE_ROW_HEIGHT = 32
const CONTAINER_HEIGHT = 275
const CONTAINER_WIDTH = 225
const PADDING = 20
const ACTION_ITEM_HEIGHT = 20

const StyledListContainer = styled.div<{ height: number }>`
  height: ${props => props.height - 2 * PADDING + 'px'};
`

const StyledDiv = styled.div<{ height: number; width: number }>`
  height: ${props => props.height + 'px'};
  width: ${props => props.width + 'px'};
  box-shadow: 5px 15px 30px 0 rgba(132, 132, 132, 0.25);
  background-color: #ffffff;
  padding: ${PADDING + 'px'};
  overflow: hidden;
`
const StyledActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface ICheckBoxRow {
  data: any
  index: number
  style: any
}

const createItemData = memoize(
  (clusterData, selectedRows, onCheckboxClick, width, addedClusters) => ({
    clusterData,
    selectedRows,
    onCheckboxClick,
    width,
    addedClusters
  })
)

const MultiSelect: React.FunctionComponent<ICheckBoxRow> = React.memo(
  ({ data, index, style }) => {
    const { clusterData, selectedRows, onCheckboxClick, width, addedClusters } = data
    const row = clusterData[index]
    const rowIndex = findIndex(selectedRows, (e: any) => e.Id === row.Id)
    const isChecked = rowIndex !== -1
    const disabled = addedClusters.some(elem => elem.id === row.Id)

    return (
      <FormControlLabel
        key={row.Id}
        id='cluster-value'
        classes={{ root: css.formControlLabel }}
        style={style}
        control={
          <Checkbox
            checked={isChecked}
            onChange={onCheckboxClick(row)}
            color='primary'
            value={row}
          />
        }
        label={
          <Label
            fontSize={FontTShirtSize.md}
            cellPadding={'0px'}
            cursor={'pointer'}
            value={row.Name}
            width={width}
          />
        }
        disabled={disabled}
      />
    )
  },
  areEqual
)

const AssortmentView = ({ excludedClusters }) => (
  <div className={css.root}>
    {excludedClusters.map(({ Id, Name }) => (
      <ListItem
        classes={{
          root: css.listItem
        }}
        key={Id}
        disableGutters
      >
        <ListItemText
          primary={
            <Tooltip title={Name}>
              <Typography className={css.listItemText}>{Name}</Typography>
            </Tooltip>
          }
        />
      </ListItem>
    ))}
  </div>
)

export const ClusterExclusionMultiSelect: React.FunctionComponent<
  ClusterExclusionMultiSelectProps
> = ({
  clusterData,
  handleApply,
  isActionInProcess,
  excludedClusters,
  showDisabledExcludedClustersPopOver,
  addedClusters,
  anchorEl,
  getAssortmentExcludedClusters
}) => {
  return useObserver(function useHook() {
    const {
      clusterExclusionMultiSelect: {
        clusterListAnchorEl,
        rtlClusterListAnchorEl,
        handleClusterPopOverClose,
        setSelectedClusters,
        onCheckboxClick,
        newSelectedClusters
      }
    } = stores
    const { t } = useTranslation()
    const open =
      Boolean(clusterListAnchorEl || rtlClusterListAnchorEl) || Boolean(anchorEl)
    const id = open ? 'cluster-exclusion-popover' : undefined
    const clusterLen = clusterData?.length

    useEffect(() => {
      if (!isEmpty(clusterData)) {
        setSelectedClusters(excludedClusters)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Clusters List
    return (
      <Popover
        id={id}
        open={open}
        anchorEl={clusterListAnchorEl || rtlClusterListAnchorEl || anchorEl} // As clusterListAnchorEl is getting from store adding or condition to pick it via props
        onClose={handleClusterPopOverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {showDisabledExcludedClustersPopOver ? (
          <AssortmentView
            excludedClusters={getAssortmentExcludedClusters?.() || excludedClusters}
          />
        ) : (
          <StyledDiv height={CONTAINER_HEIGHT} width={CONTAINER_WIDTH}>
            <StyledListContainer height={CONTAINER_HEIGHT - ACTION_ITEM_HEIGHT}>
              <AutoSizer>
                {({ height, width }) => {
                  if (width <= 0 || height <= 0) return <></>
                  const checkBoxLabelWidth = width - PADDING * 2
                  const data = createItemData(
                    clusterData,
                    newSelectedClusters,
                    onCheckboxClick,
                    checkBoxLabelWidth,
                    addedClusters
                  )
                  return clusterLen ? (
                    <List
                      itemData={data}
                      itemCount={clusterLen}
                      itemSize={_ => TABLE_ROW_HEIGHT}
                      overScanCount={5}
                      height={height}
                      width={width}
                    >
                      {MultiSelect}
                    </List>
                  ) : (
                    <NoRecordFound></NoRecordFound>
                  )
                }}
              </AutoSizer>
            </StyledListContainer>
            <StyledActionContainer>
              <Button onClick={handleApply} disabled={isActionInProcess}>
                {t('common.label.apply', 'Apply')}
                {isActionInProcess && (
                  <CircularProgress className={css.buttonProgress} size={24} />
                )}
              </Button>
              <Button onClick={handleClusterPopOverClose}>
                {t('common.label.cancel', 'Cancel')}
              </Button>
            </StyledActionContainer>
          </StyledDiv>
        )}
      </Popover>
    )
  })
}
